import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { confirmToast } from "../../lib/confirmToast";
import { formatLevel, formatPoints, formatProfession } from "../../lib/display";
import { Ban, Plus, Pencil, User, Search, Loader2, QrCode, Copy, Coins, History } from "lucide-react";
import PartnerHistoryDialog from "../../components/PartnerHistoryDialog";
import { QRCodeSVG } from "qrcode.react";

const AdminParceiros = () => {
  const [parceiros, setParceiros] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedParceiro, setSelectedParceiro] = useState(null);
  const [editing, setEditing] = useState(null);
  const [historyPartner, setHistoryPartner] = useState(null);
  const [formData, setFormData] = useState({
    loja_id: "",
    loja_ids: [],
    nome: "",
    cpf: "",
    telefone: "",
    profissao: "",
    cidade: "",
    email: "",
    codigo_parceiro_erp: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [parceirosRes, lojasRes] = await Promise.all([
        api.get("/parceiros"),
        api.get("/lojas"),
      ]);
      setParceiros(parceirosRes.data);
      setLojas(lojasRes.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar dados."));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        loja_ids: Array.from(new Set([formData.loja_id, ...formData.loja_ids].filter(Boolean))),
      };
      if (editing) {
        await api.put(`/parceiros/${editing}`, payload);
        toast.success("Parceiro atualizado com sucesso!");
      } else {
        await api.post("/parceiros", payload);
        toast.success("Parceiro criado com sucesso!");
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData({ loja_id: "", loja_ids: [], nome: "", cpf: "", telefone: "", profissao: "", cidade: "", email: "", codigo_parceiro_erp: "" });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao salvar parceiro."));
    }
  };

  const handleEdit = (parceiro) => {
    setEditing(parceiro.id);
    setFormData({
      loja_id: parceiro.loja_id,
      loja_ids: Array.from(new Set([parceiro.loja_id, ...(parceiro.loja_ids || [])].filter(Boolean))),
      nome: parceiro.nome,
      cpf: parceiro.cpf,
      telefone: parceiro.telefone,
      profissao: parceiro.profissao,
      cidade: parceiro.cidade,
      email: parceiro.email || "",
      codigo_parceiro_erp: parceiro.codigo_parceiro_erp || "",
    });
    setDialogOpen(true);
  };

  const deactivateParceiro = async (id) => {
    try {
      await api.delete(`/parceiros/${id}`);
      toast.success("Parceiro inativado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao inativar parceiro."));
    }
  };

  const setFixedStore = (lojaId) => {
    setFormData((current) => ({
      ...current,
      loja_id: lojaId,
      loja_ids: Array.from(new Set([lojaId, ...current.loja_ids].filter(Boolean))),
    }));
  };

  const togglePointStore = (lojaId) => {
    setFormData((current) => {
      if (lojaId === current.loja_id) return current;
      const lojaIds = current.loja_ids.includes(lojaId)
        ? current.loja_ids.filter((id) => id !== lojaId)
        : [...current.loja_ids, lojaId];
      return { ...current, loja_ids: lojaIds };
    });
  };

  const handleDelete = (id) => {
    confirmToast({
      title: "Inativar parceiro?",
      description: "Ele deixará de acessar a área do parceiro e não receberá novos pontos.",
      confirmLabel: "Inativar",
      variant: "danger",
      onConfirm: () => deactivateParceiro(id),
    });
  };

  const showQrCode = (parceiro) => {
    setSelectedParceiro(parceiro);
    setQrDialogOpen(true);
  };

  const copyQrUrl = (codigo) => {
    const url = `${window.location.origin}/p/${codigo}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const filteredParceiros = parceiros.filter(
    (p) =>
      p.nome.toLowerCase().includes(search.toLowerCase()) ||
      p.profissao.toLowerCase().includes(search.toLowerCase()) ||
      p.codigo_parceiro.toLowerCase().includes(search.toLowerCase()) ||
      (p.codigo_parceiro_erp || "").toLowerCase().includes(search.toLowerCase())
  );

  const profissoes = ["Pintor", "Pedreiro", "Eletricista", "Arquiteto", "Instalador", "Encanador", "Marceneiro"];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-parceiros">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Parceiros</h1>
          <p className="text-slate-600 mt-1">Gerencie os parceiros do programa</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setFormData({ loja_id: "", loja_ids: [], nome: "", cpf: "", telefone: "", profissao: "", cidade: "", email: "", codigo_parceiro_erp: "" });
              }}
              data-testid="add-parceiro-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Parceiro
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editing ? "Editar Parceiro" : "Novo Parceiro"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="loja_id">Loja fixa</Label>
                <select
                  id="loja_id"
                  value={formData.loja_id}
                  onChange={(e) => setFixedStore(e.target.value)}
                  className="input-field w-full"
                  required
                  data-testid="parceiro-loja-select"
                >
                  <option value="">Selecione uma loja</option>
                  {lojas.map((loja) => (
                    <option key={loja.id} value={loja.id}>
                      {loja.nome}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  Campanhas, recompensas e resgates deste parceiro seguem a loja fixa.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Lojas que podem gerar pontos</Label>
                <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                  {lojas.map((loja) => {
                    const isFixedStore = loja.id === formData.loja_id;
                    const checked = formData.loja_ids.includes(loja.id);
                    return (
                      <Label
                        key={loja.id}
                        htmlFor={`partner-point-store-${loja.id}`}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50"
                      >
                        <Checkbox
                          id={`partner-point-store-${loja.id}`}
                          checked={checked}
                          disabled={isFixedStore}
                          onCheckedChange={() => togglePointStore(loja.id)}
                        />
                        <span className="min-w-0 flex-1 text-sm text-slate-700">{loja.nome}</span>
                        {isFixedStore && <span className="text-xs font-medium text-primary">Fixa</span>}
                      </Label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  Vendas ERP destas lojas acumulam pontos no mesmo saldo do parceiro.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome">Nome completo</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="input-field"
                  required
                  data-testid="parceiro-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo_parceiro_erp">Código do parceiro no ERP (opcional)</Label>
                <Input
                  id="codigo_parceiro_erp"
                  value={formData.codigo_parceiro_erp}
                  onChange={(e) => setFormData({ ...formData, codigo_parceiro_erp: e.target.value })}
                  className="input-field"
                  placeholder="Ex: 12345"
                  data-testid="parceiro-codigo-erp-input"
                />
                <p className="text-xs text-slate-500">
                  Use o mesmo código que aparece no campo codigo_parceiro_erp da venda no ERP.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="input-field"
                    required
                    data-testid="parceiro-cpf-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="input-field"
                    required
                    data-testid="parceiro-telefone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="profissao">Profissão</Label>
                <select
                  id="profissao"
                  value={formData.profissao}
                  onChange={(e) => setFormData({ ...formData, profissao: e.target.value })}
                  className="input-field w-full"
                  required
                  data-testid="parceiro-profissao-select"
                >
                  <option value="">Selecione</option>
                  {profissoes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  className="input-field"
                  required
                  data-testid="parceiro-cidade-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email de acesso do parceiro (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  data-testid="parceiro-email-input"
                />
                <p className="text-xs text-slate-500">
                  Se preenchido, o parceiro acessa a área de resgates com este email. Senha inicial: últimos 4 dígitos do CPF.
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-parceiro-btn">
                  {editing ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-center">
              QR Code do Parceiro
            </DialogTitle>
          </DialogHeader>
          {selectedParceiro && (
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-2xl shadow-inner">
                <QRCodeSVG
                  value={`${window.location.origin}/p/${selectedParceiro.codigo_parceiro}`}
                  size={200}
                  level="H"
                />
              </div>
              <p className="mt-4 font-heading font-semibold text-lg text-slate-900">
                {selectedParceiro.nome}
              </p>
              <p className="text-slate-500">{selectedParceiro.codigo_parceiro}</p>
              <Button
                variant="outline"
                onClick={() => copyQrUrl(selectedParceiro.codigo_parceiro)}
                className="mt-4"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar parceiros..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
          data-testid="search-parceiros-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Parceiro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Profissão
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Pontos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Nível
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredParceiros.map((parceiro) => (
                  <tr key={parceiro.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{parceiro.nome}</p>
                          <p className="text-sm text-slate-500">{parceiro.cidade}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono">
                          {parceiro.codigo_parceiro}
                        </code>
                        {parceiro.codigo_parceiro_erp && (
                          <p className="text-xs text-slate-500">
                            ERP: <span className="font-mono">{parceiro.codigo_parceiro_erp}</span>
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{formatProfession(parceiro.profissao)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Coins className="h-4 w-4 text-yellow-500" />
                        <span className="font-semibold text-slate-900">
                          {formatPoints(parceiro.saldo_pontos)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        parceiro.nivel === "Ouro" 
                          ? "level-badge-gold" 
                          : parceiro.nivel === "Prata" 
                            ? "level-badge-silver" 
                            : "level-badge-bronze"
                      }`}>
                        {formatLevel(parceiro.nivel)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" title="Ver histórico do parceiro" aria-label="Ver histórico do parceiro" data-testid={`history-parceiro-${parceiro.id}`} onClick={() => setHistoryPartner(parceiro)}><History className="h-4 w-4" /></Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => showQrCode(parceiro)}
                          className="text-slate-500 hover:text-primary"
                          data-testid={`qr-parceiro-${parceiro.id}`}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(parceiro)}
                          className="text-slate-500 hover:text-primary"
                          data-testid={`edit-parceiro-${parceiro.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(parceiro.id)}
                          className="text-slate-500 hover:text-red-500"
                          title="Inativar parceiro"
                          aria-label="Inativar parceiro"
                          data-testid={`delete-parceiro-${parceiro.id}`}
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredParceiros.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      {search ? "Nenhum parceiro encontrado" : "Nenhum parceiro cadastrado ainda"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PartnerHistoryDialog parceiro={historyPartner} onClose={() => setHistoryPartner(null)} />
    </div>
  );
};

export default AdminParceiros;
